import { useState } from 'react'

import { config } from '@skytech/config'
import { ExternalLink, LoaderCircle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Image } from '@skytech/ui/Image'
import { sanitizeAndInsertTargetBlank } from '@skytech/utils/html-sanitization'
import ChevronRightIcon from 'mdi-react/ChevronRightIcon'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import { LocalDate } from '@/components/LocalDate'
import Error from '@/components/util/Error'
import { closeModal, openModal } from '@/ducks/modalReducer'
import {
  getHasPostError,
  getListingFromId,
  getOrdersFromListingId,
  postOrder,
} from '@/ducks/resaleReducer'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { numberFormat } from '@/util/formatters/numbers'

import './listing.scss'

const Listing = () => {
  const dispatch = useDispatch()

  const { listingId } = useParams()

  const { id, price, stock, name, images, description, url } = useSelector(state =>
    // @ts-ignore
    getListingFromId(state, parseInt(listingId))
  ) // @ts-ignore
  const orders = useSelector(state => getOrdersFromListingId(state, parseInt(listingId)))

  // @ts-ignore
  const hasError = useSelector(state => getHasPostError(state, parseInt(listingId)))

  const [isLoading, setIsLoading] = useState(false)

  const isDisabled = stock <= 0 || isLoading

  const imageList = images?.split(',') || []

  const openConfirmation = () => {
    dispatch(
      // @ts-ignore
      openModal(
        <Confirmation
          id={id}
          price={price}
          setIsLoading={setIsLoading}
          name={name}
          isDisabled={isDisabled}
        />
      )
    )
  }

  if (!id) return null

  return (
    <div className="listing">
      <h3>{name}</h3>

      <p
        dangerouslySetInnerHTML={{ __html: sanitizeAndInsertTargetBlank(description) }}
        className="description"
      />

      {!!url?.trim() && (
        <p className="url">
          <ChevronRightIcon />
          <ExternalLink href={url}>Les mer her</ExternalLink>
        </p>
      )}

      <p className="stock">Lagerstatus: {numberFormat(stock)}</p>

      <p className="price">
        <FormattedNumberWithCurrency amount={price} />
      </p>

      <Error active={hasError}>
        <b>Feil:</b> Kunne ikke opprette ordre
      </Error>

      {orders.length > 0 && (
        <ul className="orders">
          {/* @ts-ignore */}
          {orders.map(order => (
            <Order key={order.id} {...order} />
          ))}
        </ul>
      )}

      <p>
        <Button variant="primary" disabled={isDisabled} onClick={openConfirmation}>
          bestill
        </Button>
        {isLoading && <LoaderCircle />}
      </p>

      {imageList.length > 0 && (
        <div className="listing__images">
          {/* @ts-ignore */}
          {imageList.map(src => (
            <Image src={`${config.API_URL}files/uploads/resale/${src}`} key={src} alt="product" />
          ))}
        </div>
      )}
    </div>
  )
}

const Order = ({ created, price }: any) => (
  <li>
    <Translation id="Bestilt {date} for" date={<LocalDate date={created} />} />{' '}
    <FormattedNumberWithCurrency amount={price} />
  </li>
)

const Confirmation = ({ name, setIsLoading, id, price, isDisabled }: any) => {
  const dispatch = useDispatch()
  // @ts-ignore
  const close = () => dispatch(closeModal())
  const { formatNumber } = useFormattedNumberWithCurrency()

  const post = async () => {
    close()
    setIsLoading(true)
    // @ts-ignore
    await dispatch(postOrder(id, price))
    setIsLoading(false)
  }

  return (
    <div>
      <h3>Bekreft bestilling</h3>

      <p>
        <Translation
          id="Er du sikker på at du ønsker å bestille {name} for {amount}?"
          name={name}
          amount={formatNumber({ amount: price })}
        />
      </p>

      <Button variant="tertiary" onClick={close}>
        avbryt
      </Button>

      <Button onClick={post} disabled={isDisabled}>
        bekreft
      </Button>
    </div>
  )
}

export default Listing
